import { goToKeys, ShortcutKeys, screenActions, themeCommand } from "@nexxt/app-kit";
import { CommandPalette, type PaletteGroup, type PaletteItem } from "@nexxt/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Building2, Layers, Megaphone, Moon, Plus, Store } from "lucide-react";
import { useState } from "react";
import { m } from "../../paraglide/messages.js";
import { accountQueries } from "../features/account/account.queries";
import { accountCode } from "../features/account/account-code";
import { useCan } from "../features/auth/me";
import { brandKey } from "../features/brand/brands";
import { useBrandLens } from "../features/brand/use-brand-lens";
import { campaignQueries } from "../features/campaign/campaign.queries";
import type { Place } from "./places";

// ⌘K of Guestlist (ADR-306, design-system.md §4.10): the actions of the screen on display, a new email, the places
// with their `g` keys, the lens of the brand, and with a text the campaigns and the accounts crm finds by name, then
// the theme. The brands, to look through or to open, show only once something is typed: there are dozens.

/** A search starts from two letters: one would find half the accounts. */
const MIN_SEARCH = 2;
const FOUND_RANK = 0.9;

interface CommandMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  places: readonly Place[];
}

export function CommandMenu({ open, onOpenChange, places }: CommandMenuProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const q = search.trim();
  const asked = open && q.length >= MIN_SEARCH;
  const canWrite = useCan("crm.template.write");
  const readsCampaigns = useCan("crm.campaign.read");
  const readsAccounts = useCan("crm.account.read");
  const campaigns = useQuery({ ...campaignQueries.find(q), enabled: asked && readsCampaigns });
  const accounts = useQuery({ ...accountQueries.find(q), enabled: asked && readsAccounts });
  const lens = useBrandLens();
  const theme = themeCommand();

  const lensItem = (value: typeof lens.value, label: string): PaletteItem => ({
    id: `lens-${value}`,
    label: m.palette_lens({ lens: label }),
    keywords: [label],
    icon: <Layers />,
    run: () => lens.choose(value),
  });

  const groups: PaletteGroup[] = [
    { heading: m.palette_actions(), items: open ? screenActions() : [] },
    {
      heading: m.palette_create(),
      items: canWrite
        ? [
            {
              id: "new-email",
              label: m.email_new(),
              icon: <Plus />,
              run: () => void navigate({ to: "/emails", search: { new: true } }),
            },
          ]
        : [],
    },
    {
      heading: m.palette_go_to(),
      items: places.map((place) => ({
        id: `go-${place.to}`,
        label: place.label(),
        icon: <place.icon />,
        shortcut: <ShortcutKeys keys={goToKeys(place.key)} />,
        run: () => void navigate({ to: place.to }),
      })),
    },
    {
      heading: m.lens_label(),
      items: [
        ...(lens.lens.kind === "all" ? [] : [lensItem("all", m.lens_all())]),
        ...(lens.lens.kind === "mine" ? [] : [lensItem("mine", m.lens_mine())]),
        ...(q === "" ? [] : lens.brands.filter((b) => b.id !== lens.value).map((b) => lensItem(b.id, b.name))),
      ],
    },
    {
      // the page of a brand, as the lens: only once something is typed
      heading: m.nav_brands(),
      items: (q === "" ? [] : [{ id: null, name: m.brand_tomorrow() }, ...lens.brands]).map((brand) => ({
        id: `brand-${brandKey(brand.id)}`,
        label: m.palette_open_brand({ brand: brand.name }),
        keywords: [brand.name],
        icon: <Store />,
        run: () => void navigate({ to: "/brands/$brandOrgId", params: { brandOrgId: brandKey(brand.id) } }),
      })),
    },
    {
      heading: m.nav_campaigns(),
      items: (asked ? (campaigns.data?.items ?? []) : []).map((campaign) => ({
        id: `campaign-${campaign.id}`,
        label: campaign.name,
        icon: <Megaphone />,
        rank: FOUND_RANK,
        run: () => void navigate({ to: "/campaigns/$campaignId", params: { campaignId: campaign.id } }),
      })),
    },
    {
      heading: m.nav_accounts(),
      items: (asked ? (accounts.data?.items ?? []) : []).map((account) => {
        const code = accountCode(account);
        return {
          id: `account-${account.id}`,
          label: account.name,
          ...(code && { hint: code }),
          icon: <Building2 />,
          rank: FOUND_RANK,
          run: () => void navigate({ to: "/accounts/$organizationId", params: { organizationId: account.id } }),
        };
      }),
    },
    { heading: m.palette_preferences(), items: [{ id: "theme", label: theme.label, icon: <Moon />, run: theme.run }] },
  ];

  return (
    <CommandPalette
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setSearch("");
      }}
      groups={groups}
      label={m.palette_label()}
      placeholder={m.palette_placeholder()}
      emptyText={campaigns.isFetching || accounts.isFetching ? m.palette_searching() : m.palette_empty()}
      search={search}
      onSearchChange={setSearch}
    />
  );
}
